import { useState } from "react";
import { apiFetch } from "../../../context/AuthContext";
import { getToken, decodeJwtPayload } from "../utils/tokenUtils";

const useVisibilityRules = ({ showAlert, t } = {}) => {
  const API_BASE_URL = process.env.REACT_APP_BASE_URL;

  // Fallbacks for when showAlert / t are not provided
  const _alert = showAlert || ((title, msg, type) => console.log(`[${type}] ${title}: ${msg}`));
  const _t = t || ((key) => key);

  // Visibility Rules State (UI removed; keep data state used for filtering)
  const [visibilityRules, setVisibilityRules] = useState([]);
  const [loadingVisibility, setLoadingVisibility] = useState(false);
  const [visibilityError, setVisibilityError] = useState(null);

  // ==================== VISIBILITY RULES FUNCTIONS ====================

  // Fetch visibility rules (prefers admin->employees endpoint, falls back to rules)
  const fetchVisibilityRules = async () => {
    setLoadingVisibility(true);
    setVisibilityError(null);
    try {
      // Robustly resolve current user's infinity code from several fallbacks
      const userDataStr =
        localStorage.getItem("user") || sessionStorage.getItem("user");
      let currentAdminInfinityCode = null;

      if (userDataStr) {
        try {
          const userData = JSON.parse(userDataStr);
          currentAdminInfinityCode =
            userData.infinityCode ||
            userData.employeeInfinityCode ||
            userData.id ||
            userData.infinity_code;
        } catch (e) {
          // ignore parse error
        }
      }

      // Fallback: try to decode JWT token payload to extract infinityCode
      if (!currentAdminInfinityCode) {
        const token = getToken();
        if (token) {
          try {
            const payload = decodeJwtPayload(token);
            currentAdminInfinityCode =
              payload?.infinityCode ||
              payload?.infinity_code ||
              payload?.id ||
              payload?.employeeInfinityCode ||
              null;
          } catch (e) {
            // ignore
          }
        }
      }

      if (!currentAdminInfinityCode) {
        throw new Error("Admin infinity code not found");
      }

      // Normalize to Number when used in queries
      const adminCodeNum = String(currentAdminInfinityCode);
      // Do not setSelectedAdmin here - UI for selecting admin was removed

      // First, try the endpoint that returns visible employees directly
      try {
        const resp = await apiFetch(
          `${API_BASE_URL}/payrollX/visibility/admin/${adminCodeNum}/employees`,
          { method: "GET" },
        );

        if (resp.ok) {
          const body = await resp.json();
          // Map to rule-like objects to keep existing shape used elsewhere
          const mapped = (body.data || []).map((emp) => ({
            adminInfinityCode: adminCodeNum,
            employeeInfinityCode: emp.infinityCode,
            employee: emp,
          }));
          setVisibilityRules(mapped);
          return;
        }
      } catch (err) {
        // ignore and fallback to rules endpoint
        console.warn(
          "Visibility employees endpoint failed, falling back to rules endpoint",
          err,
        );
      }

      // Fallback: fetch rules filtered by admin
      const response = await apiFetch(
        `${API_BASE_URL}/payrollX/visibility/rules?adminInfinityCode=${adminCodeNum}`,
        { method: "GET" },
      );

      if (response.ok) {
        const result = await response.json();
        setVisibilityRules(result.data || []);
      } else {
        throw new Error("Failed to fetch visibility rules");
      }
    } catch (err) {
      console.error("Error fetching visibility rules:", err);
      setVisibilityError(err.message);
    } finally {
      setLoadingVisibility(false);
    }
  };

  // Note: admin/employee list fetching removed (Manage Visibility UI removed)

  // Add visibility rule
  const addVisibilityRule = async (adminInfinityCode, employeeInfinityCode) => {
    try {
      const response = await apiFetch(
        `${API_BASE_URL}/payrollX/visibility/rules`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            adminInfinityCode: String(adminInfinityCode),
            employeeInfinityCode: String(employeeInfinityCode),
          }),
        },
      );

      if (response.ok) {
        _alert(_t("success"), _t("visibilityAdded"), "success");
        fetchVisibilityRules(); // Refresh the list
        return true;
      } else {
        const error = await response.json();
        _alert(
          _t("error"),
          error.error || "Failed to add visibility rule",
          "error",
        );
        return false;
      }
    } catch (err) {
      console.error("Error adding visibility rule:", err);
      _alert(_t("error"), err.message, "error");
      return false;
    }
  };

  // Remove visibility rule
  const removeVisibilityRule = async (
    adminInfinityCode,
    employeeInfinityCode,
  ) => {
    try {
      const response = await apiFetch(
        `${API_BASE_URL}/payrollX/visibility/rules/${adminInfinityCode}/${employeeInfinityCode}`,
        {
          method: "DELETE",
        },
      );

      if (response.ok) {
        _alert(_t("success"), _t("visibilityRemoved"), "success");
        fetchVisibilityRules(); // Refresh the list
        return true;
      } else {
        _alert(_t("error"), "Failed to remove visibility rule", "error");
        return false;
      }
    } catch (err) {
      console.error("Error removing visibility rule:", err);
      _alert(_t("error"), err.message, "error");
      return false;
    }
  };

  // Bulk add visibility rules
  const bulkAddVisibilityRules = async (
    adminInfinityCode,
    employeeInfinityCodes,
  ) => {
    try {
      const response = await apiFetch(
        `${API_BASE_URL}/payrollX/visibility/rules/bulk`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            adminInfinityCode: String(adminInfinityCode),
            employeeInfinityCodes: employeeInfinityCodes.map(Number),
          }),
        },
      );

      if (response.ok) {
        const result = await response.json();
        _alert(
          _t("success"),
          result.message || `Added ${result.data.added} visibility rules`,
          "success",
        );
        fetchVisibilityRules(); // Refresh the list
        return true;
      } else {
        _alert(_t("error"), "Failed to bulk add visibility rules", "error");
        return false;
      }
    } catch (err) {
      console.error("Error bulk adding visibility rules:", err);
      _alert(_t("error"), err.message, "error");
      return false;
    }
  };

  // Helpers to check visibility (used by filtering payrolls)
  const getVisibleEmployeeCodes = () => {
    return visibilityRules.map((r) => String(r.employeeInfinityCode));
  };

  const isEmployeeVisible = (employeeInfinityCode) => {
    const codes = getVisibleEmployeeCodes();
    return codes.includes(String(employeeInfinityCode));
  };

  // Load visibility rules (UI removed)
  const openVisibilityModal = () => {
    // legacy function name retained for any callers; now only loads visibility rules
    fetchVisibilityRules();
  };

  // ==================== END VISIBILITY RULES FUNCTIONS ====================


  return {
    visibilityRules,
    setVisibilityRules,
    loadingVisibility,
    visibilityError,
    fetchVisibilityRules,
    addVisibilityRule,
    removeVisibilityRule,
    bulkAddVisibilityRules,
    getVisibleEmployeeCodes,
    isEmployeeVisible,
    openVisibilityModal,
  };
};

export default useVisibilityRules;